import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, Calendar, Crown, BookOpen, Heart, Volume2, VolumeX } from "lucide-react";
import { api, LOGO_DARK, formatZAR, resolveUrl } from "../lib/api";

const HERO_BG = "https://customer-assets.emergentagent.com/job_logo-site-1/artifacts/8aaz6bym_Website%20photo.jpeg";

export default function Home() {
  const [featured, setFeatured] = useState(null);
  const [artists, setArtists] = useState([]);
  const [events, setEvents] = useState([]);
  const [blog, setBlog] = useState([]);
  const [stats, setStats] = useState({});
  const [partners, setPartners] = useState([]);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    (async () => {
      const [fv, ar, ev, bl, st, pt] = await Promise.all([
        api.get("/videos/featured"),
        api.get("/artists", { params: { featured: true } }),
        api.get("/events", { params: { upcoming: true } }),
        api.get("/blog"),
        api.get("/impact-stats"),
        api.get("/partners"),
      ]);
      setFeatured(fv.data);
      setArtists(ar.data);
      setEvents(ev.data.slice(0, 3));
      setBlog(bl.data.slice(0, 3));
      setStats(st.data);
      setPartners(pt.data);
    })().catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[640px] overflow-hidden" data-testid="home-hero">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="grain absolute inset-0" />

        <div className="relative z-10 max-w-7xl mx-auto h-full px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-20 lg:pb-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/30 text-white/90 text-[11px] tracking-[0.3em] uppercase mb-8 backdrop-blur-sm fade-in">
              <span className="w-1.5 h-1.5 bg-[#C00000] rounded-full" />
              Next Gen Praise NPC — 2026
            </div>
            <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight fade-in">
              Praise
              <br />
              Beyond <span className="text-[#C00000] italic font-normal">Limits.</span>
            </h1>
            <p className="mt-8 text-white/80 text-lg max-w-xl leading-relaxed fade-in">
              A gospel music platform, content hub and community raising a generation of anointed
              worshippers across South Africa — and beyond.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 fade-in">
              <Link to="/videos" className="btn-primary-red" data-testid="action-watch-video-button">
                <Play className="w-4 h-4" /> Watch Our Latest Video
              </Link>
              <Link to="/membership" className="btn-outline-white" data-testid="action-join-community-button">
                Join the Community <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 right-8 hidden lg:flex items-center gap-3 text-white/60 text-xs tracking-[0.3em] uppercase z-10">
          <div className="h-px w-12 bg-white/40" />
          Durban · South Africa
        </div>
      </section>

      {/* FEATURED VIDEO */}
      {featured && (
        <section className="bg-[#0B1526] text-white py-20 sm:py-28 relative" data-testid="home-featured-video">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-5">
                <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-5">Latest Release</div>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mb-6">{featured.title}</h2>
                <p className="text-white/70 text-base leading-relaxed mb-3">
                  By <span className="text-white">{featured.artist_name || "Next Gen Praise"}</span>
                </p>
                <p className="text-white/60 text-sm leading-relaxed mb-8">{featured.description}</p>
                <div className="flex gap-3">
                  <Link to="/videos" className="btn-primary-red" data-testid="action-explore-videos-button">
                    Explore Videos <ArrowRight className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => setMuted((v) => !v)}
                    className="btn-outline-white"
                    data-testid="action-toggle-mute-button"
                  >
                    {muted ? <><VolumeX className="w-4 h-4" /> Unmute</> : <><Volume2 className="w-4 h-4" /> Mute</>}
                  </button>
                </div>
              </div>
              <div className="lg:col-span-7">
                <div className="relative aspect-video bg-black border border-white/10 video-embed">
                  <iframe
                    title={featured.title}
                    src={`https://www.youtube.com/embed/${featured.youtube_id}?autoplay=1&mute=${muted ? 1 : 0}&controls=1&rel=0`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ABOUT */}
      <section className="py-20 sm:py-28" data-testid="home-about">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-5">About Us</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#1F3864] mb-6">
                A platform where worship <span className="italic">becomes</span> a movement.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-6 text-gray-700 leading-relaxed">
              <p className="text-lg">
                Next Gen Praise NPC is a registered South African non-profit dedicated to raising,
                equipping and releasing anointed gospel artists into their God-given calling.
              </p>
              <p>
                We exist at the intersection of excellence and anointing — producing cinematic music
                and content, developing artists in a world of royalties and contracts, and gathering
                communities to encounter the Kingdom through praise.
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 text-[#C00000] hover:text-[#990000] font-medium tracking-wide" data-testid="action-read-more-about-button">
                Read Our Story <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ARTISTS CAROUSEL */}
      <section className="bg-[#F5F5F5] py-20 sm:py-28" data-testid="home-artists">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">Featured Artists</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight text-[#1F3864]">Voices of a generation.</h2>
            </div>
            <Link to="/artists" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#1F3864] hover:text-[#C00000]" data-testid="action-view-all-artists">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="scroll-row stagger">
            {artists.map((a) => (
              <Link
                key={a.id}
                to={`/artists/${a.id}`}
                className="image-card relative w-[280px] sm:w-[320px] aspect-[3/4] block group"
                data-testid={`artist-card-${a.id}`}
              >
                <img src={resolveUrl(a.photo_url)} alt={a.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1526] via-[#0B1526]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#C00000] mb-2">{a.genre}</div>
                  <div className="font-display text-2xl leading-tight">{a.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="py-20 sm:py-28" data-testid="home-events">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">Upcoming</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight text-[#1F3864]">Come worship with us.</h2>
            </div>
            <Link to="/events" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#1F3864] hover:text-[#C00000]">
              All Events <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5 stagger">
            {events.map((e) => (
              <div key={e.id} className="group border border-gray-200 hover:border-[#1F3864] transition-colors bg-white" data-testid={`event-card-${e.id}`}>
                <div className="aspect-[16/10] bg-gray-100 overflow-hidden">
                  <img src={resolveUrl(e.image_url)} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#C00000] mb-3">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(e.event_date).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                  <h3 className="font-display text-xl text-[#1F3864] mb-2 leading-tight">{e.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{e.venue}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm font-semibold text-[#1F3864]">
                      {e.ticket_price > 0 ? formatZAR(e.ticket_price) : "Free"}
                    </div>
                    <Link to="/events" className="text-sm text-[#C00000] font-medium hover:text-[#990000]">Details →</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEMBERSHIP BANNER */}
      <section className="relative py-24 bg-[#1F3864] text-white overflow-hidden" data-testid="home-membership-banner">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#C00000]/20 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Crown className="w-10 h-10 text-[#C00000] mx-auto mb-6" />
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight">
            Go deeper. Become a member.
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto text-lg mb-10 leading-relaxed">
            Exclusive videos, early releases, behind-the-scenes, devotionals and member-only
            discounts on merchandise and events.
          </p>
          <Link to="/membership" className="btn-primary-red" data-testid="action-membership-join-button">
            View Membership Plans <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* IMPACT STATS */}
      <section className="py-20 sm:py-24 bg-[#F5F5F5]" data-testid="home-impact-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { label: "Events Hosted", value: stats.events_hosted },
            { label: "Artists Developed", value: stats.artists_developed },
            { label: "Communities Reached", value: stats.communities_reached },
            { label: "Members Strong", value: stats.total_members },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <div className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#1F3864] mb-2">
                {s.value ?? "—"}
              </div>
              <div className="text-xs tracking-[0.2em] uppercase text-gray-600">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BLOG */}
      <section className="py-20 sm:py-28" data-testid="home-blog">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">Latest From The Journal</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight text-[#1F3864]">Read. Reflect. Worship.</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8 stagger">
            {blog.map((p) => (
              <article key={p.id} className="group" data-testid={`blog-card-${p.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-gray-100 mb-5">
                  <img src={resolveUrl(p.cover_image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-3">
                  <BookOpen className="w-3 h-3" />
                  {new Date(p.created_at).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}
                </div>
                <h3 className="font-display text-xl text-[#1F3864] mb-2 leading-snug group-hover:text-[#C00000] transition-colors">{p.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{p.excerpt}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      {partners.length > 0 && (
        <section className="py-14 bg-white border-t border-gray-200" data-testid="home-partners">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-xs tracking-[0.3em] uppercase text-gray-500 mb-8">In Partnership With</div>
            <div className="overflow-hidden">
              <div className="marquee">
                {[...partners, ...partners].map((p, i) => (
                  <img key={i} src={p.logo_url} alt={p.name} className="h-12 opacity-70 grayscale hover:grayscale-0 hover:opacity-100 transition" />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* DONATE CTA */}
      <section className="relative py-24 bg-[#0B1526] text-white overflow-hidden" data-testid="home-donate-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-10 h-10 text-[#C00000] mx-auto mb-6" />
          <h2 className="font-display text-4xl sm:text-5xl mb-5">Help us raise the next generation.</h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
            Every rand you give goes directly into developing young gospel artists, staging free
            community events, and delivering world-class worship content.
          </p>
          <Link to="/donate" className="btn-primary-red" data-testid="action-donate-cta-button">
            Give Today <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
